import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Download, FileText, CheckCircle2, ArrowRight, Zap,
  Shield, Clock, BookOpen, Mail, Loader2, Star
} from 'lucide-react';
import { toast } from 'sonner';
import { analytics } from '../lib/ga4';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GuideDownloadPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [downloaded, setDownloaded] = useState(false);

  const handleDownload = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Veuillez entrer votre email');
      return;
    }

    setLoading(true);
    try {
      // Register the lead
      await fetch(`${API_URL}/api/partners/guides/download`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, guide: 'efacturation-2026' })
      });

      // Track guide download in GA4
      analytics.trackGuideDownload();

      // Trigger download
      setDownloaded(true);
      toast.success('Guide envoyé par email !');
      
      // Download PDF directly
      const link = document.createElement('a');
      link.href = '/guides/guide-efacturation-2026.pdf';
      link.download = 'Guide-E-Facturation-2026.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      // Still show success for UX, email might fail but we want to give the guide
      setDownloaded(true);
      toast.success('Téléchargement en cours...');
    } finally {
      setLoading(false);
    }
  };

  const guideContents = [
    "Qu'est-ce que l'e-facturation et pourquoi c'est obligatoire",
    "Calendrier 2026 : Les dates clés à retenir",
    "Les 3 formats acceptés : Factur-X, UBL, CII",
    "Chorus Pro : Comment ça marche",
    "Checklist complète de mise en conformité",
    "Les erreurs à éviter absolument",
    "Comment choisir sa solution de facturation",
    "Modèles de factures conformes inclus"
  ];

  const benefits = [
    { icon: Clock, text: "Lecture en 15 minutes" },
    { icon: CheckCircle2, text: "Checklist actionnable" },
    { icon: FileText, text: "Modèles inclus" },
    { icon: Shield, text: "Mis à jour 2026" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-sm border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-cyan-500 flex items-center justify-center">
              <Zap className="h-6 w-6 text-white" />
            </div>
            <span className="text-xl font-bold text-slate-900">SmartFacture AI</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/simulateur">
              <Button variant="ghost">Test gratuit</Button>
            </Link>
            <Link to="/register">
              <Button className="bg-blue-600 hover:bg-blue-700">Essai gratuit</Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-600 via-teal-600 to-cyan-700" />
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-10 w-72 h-72 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-10 left-10 w-96 h-96 bg-yellow-300 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 py-16 md:py-24">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left: Content */}
            <div>
              <Badge className="bg-white/20 text-white border-white/30 mb-6">
                <BookOpen className="h-4 w-4 mr-2" />
                Guide Gratuit 2026
              </Badge>
              
              <h1 className="text-3xl md:text-5xl font-bold text-white mb-6 leading-tight">
                Le Guide Complet de
                <span className="block text-yellow-300">l'E-Facturation 2026</span>
              </h1>
              
              <p className="text-lg text-emerald-100 mb-8">
                Tout ce que les TPE/PME doivent savoir pour se conformer à l'obligation 
                d'e-facturation. <strong className="text-white">40 pages</strong> de conseils 
                pratiques et de modèles prêts à l'emploi.
              </p>

              {/* Benefits */}
              <div className="grid grid-cols-2 gap-4 mb-8">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center gap-2 text-white/90">
                    <benefit.icon className="h-5 w-5 text-yellow-300" />
                    <span>{benefit.text}</span>
                  </div>
                ))}
              </div>

              {/* Stars - removed fake reviews */}
              <div className="flex items-center gap-2 text-white/80">
                <span>Conformité e-facturation 2026 garantie</span>
              </div>
            </div>

            {/* Right: Download Form */}
            <div>
              <Card className="bg-white shadow-2xl">
                <CardContent className="p-8">
                  {downloaded ? (
                    <div className="text-center py-8">
                      <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
                        <CheckCircle2 className="h-10 w-10 text-emerald-600" />
                      </div>
                      <h3 className="text-2xl font-bold mb-3" style={{ color: '#0f172a' }}>
                        Guide envoyé !
                      </h3>
                      <p className="text-slate-600 mb-6">
                        Vérifiez votre boîte mail. Le guide arrive dans quelques secondes.
                      </p>
                      <div className="space-y-3">
                        <Link to="/simulateur">
                          <Button className="w-full bg-emerald-600 hover:bg-emerald-700">
                            Tester ma conformité
                            <ArrowRight className="ml-2 h-4 w-4" />
                          </Button>
                        </Link>
                        <Link to="/register">
                          <Button variant="outline" className="w-full">
                            Créer mon compte gratuit
                          </Button>
                        </Link>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="text-center mb-6">
                        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-100 to-teal-100 flex items-center justify-center mx-auto mb-4">
                          <Download className="h-8 w-8 text-emerald-600" />
                        </div>
                        <h2 className="text-2xl font-bold text-slate-900 mb-2">
                          Téléchargez le guide gratuit
                        </h2>
                        <p className="text-slate-600">
                          Entrez votre email pour recevoir le guide complet
                        </p>
                      </div>

                      <form onSubmit={handleDownload} className="space-y-4">
                        <div>
                          <div className="relative">
                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                            <Input
                              type="email"
                              value={email}
                              onChange={(e) => setEmail(e.target.value)}
                              placeholder="votre@email.com"
                              className="pl-10 h-12 text-lg"
                              required
                            />
                          </div>
                        </div>

                        <Button 
                          type="submit" 
                          className="w-full h-12 text-lg bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700"
                          disabled={loading}
                        >
                          {loading ? (
                            <>
                              <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                              Envoi en cours...
                            </>
                          ) : (
                            <>
                              <Download className="h-5 w-5 mr-2" />
                              Télécharger le guide
                            </>
                          )}
                        </Button>

                        <p className="text-xs text-slate-500 text-center">
                          Vos données sont protégées. Pas de spam, promis.
                        </p>
                      </form>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* What's Inside */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="bg-emerald-100 text-emerald-700 mb-4">Contenu</Badge>
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Ce que vous allez apprendre
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {guideContents.map((content, index) => (
              <Card key={index} className="border-slate-200 hover:border-emerald-200 transition-colors">
                <CardContent className="p-4 flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-emerald-700 font-bold">{index + 1}</span>
                  </div>
                  <span className="text-slate-700">{content}</span>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-slate-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">
            Prêt à vous mettre en conformité ?
          </h2>
          <p className="text-slate-600 mb-8">
            SmartFacture AI vous accompagne de A à Z dans votre transition vers l'e-facturation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/simulateur">
              <Button size="lg" variant="outline">
                Tester ma conformité
              </Button>
            </Link>
            <Link to="/register">
              <Button size="lg" className="bg-blue-600 hover:bg-blue-700">
                Essai gratuit 7 jours
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GuideDownloadPage;
